import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { supabase } from "@/lib/supabase";

const US_STATES = [
  "New Jersey", "New York", "Pennsylvania", "Connecticut",
  "Delaware", "Maryland", "Massachusetts", "Rhode Island",
];

export default function SettingsPage() {
  const [states, setStates] = useState<string[]>([]);
  const [zips, setZips] = useState<string[]>([]);
  const [zipInput, setZipInput] = useState("");
  const [minScore, setMinScore] = useState(75);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setError("Not authenticated"); setLoading(false); return; }

      const { data, error: fetchError } = await supabase
        .from("installers")
        .select("service_states, service_zips, min_lead_score")
        .eq("id", user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (data) {
        setStates(data.service_states || []);
        setZips(data.service_zips || []);
        setMinScore(data.min_lead_score ?? 75);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const toggleState = (s: string) =>
    setStates(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const addZip = () => {
    if (/^\d{5}$/.test(zipInput) && !zips.includes(zipInput)) {
      setZips(p => [...p, zipInput]);
      setZipInput("");
    }
  };

  const removeZip = (z: string) => setZips(p => p.filter(x => x !== z));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: updateError } = await supabase
        .from("installers")
        .update({
          service_states: states,
          service_zips: zips,
          min_lead_score: minScore,
        })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your service area and lead preferences</p>
        </div>

        {saved && (
          <div className="bg-green-50 border border-green-300 rounded-xl px-5 py-3 text-green-800 text-sm font-medium flex items-center gap-2">
            <i className="ri-check-line"></i> Settings saved successfully!
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm flex items-center gap-2">
            <i className="ri-error-warning-line"></i> {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#16a34a] border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-sm text-gray-400">Loading your settings...</span>
          </div>
        ) : (
          <>
            {/* Service States */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">Service States</h2>
                <p className="text-xs text-gray-400 mt-0.5">Select the states where you accept leads</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {US_STATES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleState(s)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition cursor-pointer text-left whitespace-nowrap ${
                      states.includes(s)
                        ? "bg-[#16a34a] text-white border-[#16a34a]"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:border-green-400"
                    }`}
                  >
                    {states.includes(s) && <i className="ri-check-line mr-1"></i>}
                    {s}
                  </button>
                ))}
              </div>
              {states.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <i className="ri-alert-line"></i> No states selected — you won&apos;t receive any leads
                </p>
              )}
            </div>

            {/* ZIP Codes */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">Target ZIP Codes</h2>
                <p className="text-xs text-gray-400 mt-0.5">Optionally narrow down to specific ZIP codes</p>
              </div>
              <div className="flex gap-2">
                <input
                  value={zipInput}
                  onChange={e => setZipInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addZip())}
                  placeholder="e.g. 90210"
                  maxLength={5}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={addZip}
                  className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition cursor-pointer whitespace-nowrap"
                >
                  Add ZIP
                </button>
              </div>
              {zips.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {zips.map(z => (
                    <span
                      key={z}
                      className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {z}
                      <button
                        onClick={() => removeZip(z)}
                        className="hover:text-red-500 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No ZIP codes added — all ZIPs in selected states are eligible</p>
              )}
            </div>

            {/* Min Lead Score */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">Minimum Lead Score</h2>
                <p className="text-sm text-gray-500">Only receive leads at or above this score threshold.</p>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={50}
                  max={99}
                  value={minScore}
                  onChange={e => setMinScore(Number(e.target.value))}
                  className="flex-1 accent-green-600"
                />
                <span className="w-12 text-center font-bold text-[#16a34a] text-lg">{minScore}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>50 (All leads)</span>
                <span>99 (Top only)</span>
              </div>
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-500">
                {minScore <= 60 && "You&apos;ll see all leads including lower-quality ones."}
                {minScore > 60 && minScore <= 75 && "Good balance — you&apos;ll see most leads with decent quality."}
                {minScore > 75 && minScore <= 89 && "High quality filter — only strong leads will appear."}
                {minScore >= 90 && "Elite filter — only the very best leads will be shown to you."}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
